// src/pages/finances/BilanMensuel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Spinner, Alert, Button, Table, Form, Badge, InputGroup, Pagination } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';
import Sidebar from '../../layouts/Sidebar';
import { Colors } from 'chart.js';

// ===== STYLES =====
const styles = `
  .dashboard-styled {
    background: #f4f6f9;
    min-height: 100vh;
  }

  .header-modern {
    background: white;
    padding: 1.2rem 1.8rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    border-left: 4px solid ${COLORS.secondary};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
    background: ${COLORS.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: white;
    font-size: 1.4rem;
  }

  .gradient-text {
    color: #1e293b;
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0;
  }

  .badge-modern {
    background: #f1f4f9;
    color: #475569;
    padding: 0.3rem 0.8rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .badge-modern i {
    color: ${COLORS.secondary};
  }

  .btn-modern-outline {
    border: 1px solid #d1d5db;
    background: white;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #4b5563;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-modern-outline:hover {
    border-color: ${COLORS.secondary};
    color: ${COLORS.secondary};
    background: #fafafa;
  }

  .btn-modern-primary {
    background: ${COLORS.primary};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-primary:hover {
    background: ${COLORS.primaryDark};
    box-shadow: 0 4px 10px rgba(41,90,167,0.3);
  }

  .btn-modern-success {
    background: ${COLORS.success};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-success:hover {
    background: #059669;
    box-shadow: 0 4px 10px rgba(16,185,129,0.3);
  }

  .kpi-card {
    background: white;
    border-radius: 4px;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    transition: all 0.2s ease;
    height: 100%;
    border-left: 3px solid;
  }
  .kpi-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.06);
  }
  .kpi-success { border-left-color: ${COLORS.success}; }
  .kpi-danger { border-left-color: ${COLORS.danger}; }
  .kpi-primary { border-left-color: ${COLORS.primary}; }
  .kpi-purple { border-left-color: #8b5cf6; }

  .kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
  }
  .kpi-success .kpi-icon { background: ${COLORS.success}15; color: ${COLORS.success}; }
  .kpi-danger .kpi-icon { background: ${COLORS.danger}15; color: ${COLORS.danger}; }
  .kpi-primary .kpi-icon { background: ${COLORS.primary}15; color: ${COLORS.primary}; }
  .kpi-purple .kpi-icon { background: #8b5cf615; color: #8b5cf6; }

  .kpi-content { flex: 1; }
  .kpi-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    color: #6c757d;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
  .kpi-value {
    font-size: 1rem;
    font-weight: 700;
    margin: 0.1rem 0 0 0;
    color: #1e293b;
  }

  .filter-card {
    border: none;
    border-radius: 4px;
    background: ${COLORS.primary};
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  }
  .filter-card .form-control,
  .filter-card .form-select {
    border-radius: 4px;
    border: none;
    padding: 0.35rem 0.7rem;
    font-size: 0.8rem;
    background: rgba(255,255,255,0.95);
  }
  .filter-card .form-control:focus,
  .filter-card .form-select:focus {
    box-shadow: 0 0 0 3px rgba(255,255,255,0.2);
  }

  .table-card {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    overflow: hidden;
  }
  .table-header-secondary {
    background: ${COLORS.secondary};
    color: white;
    padding: 0.6rem 1rem;
  }
  .table-modern thead th {
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }
  .table-modern tbody tr {
    transition: all 0.2s ease;
  }
  .table-modern tbody tr:hover {
    background: #f8fafc;
  }

  .reference-badge {
    background: #f1f4f9;
    padding: 0.2rem 0.6rem;
    border-radius: 4px;
    font-size: 0.7rem;
    font-family: monospace;
    color: #334155;
  }

  .chart-card {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    border: 1px solid #e9edf2;
  }
  .chart-card-header {
    background: transparent;
    border-bottom: 1px solid #e9edf2;
    padding: 0.6rem 1rem;
    font-weight: 600;
    font-size: 0.85rem;
  }

  .progress-bar-container {
    width: 100%;
    height: 6px;
    background: #e9edf2;
    border-radius: 4px;
    overflow: hidden;
  }
  .progress-bar-fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.3s ease;
  }

  .loading-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
  }
  .loading-ring-wrapper {
    position: relative;
    width: 70px;
    height: 70px;
  }
  .loading-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 3px solid #e9edf2;
    border-top-color: ${COLORS.primary};
    animation: spin 0.8s linear infinite;
  }
  .loading-ring-inner {
    position: absolute;
    top: 8px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    border-radius: 50%;
    border: 2px solid #e9edf2;
    border-bottom-color: ${COLORS.secondary};
    animation: spin 0.6s linear infinite reverse;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .fade-in {
    animation: fadeIn 0.3s ease-out;
  }

  @media (max-width: 768px) {
    .header-modern {
      flex-direction: column;
      align-items: stretch;
      gap: 0.8rem;
      padding: 1rem;
    }
    .header-left {
      flex-wrap: wrap;
    }
    .kpi-card {
      padding: 0.8rem;
    }
  }
`;

// ===== UTILITAIRES =====
const formatMontant = (montant) => {
  return new Intl.NumberFormat('fr-FR').format(montant) + ' Fcfa';
};

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long' };
  return new Date(dateString).toLocaleDateString('fr-FR', options);
};

export default function BilanMensuel() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [temples, setTemples] = useState([]);
  const [selectedTemple, setSelectedTemple] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [bilanData, setBilanData] = useState({
    entrees: [],
    depenses: [],
    totalEntrees: 0,
    totalDepenses: 0,
    solde: 0,
    parType: {
      entrees: {},
      depenses: {}
    }
  });

  // États pour la recherche et la pagination des tableaux
  const [searchEntree, setSearchEntree] = useState('');
  const [searchDepense, setSearchDepense] = useState('');
  const [currentPageEntree, setCurrentPageEntree] = useState(1);
  const [currentPageDepense, setCurrentPageDepense] = useState(1);
  const itemsPerPage = 5;

  const mois = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear - 5; i <= currentYear + 2; i++) years.push(i);

  const fetchTemples = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const response = await fetch(`https://josephsbusiness.com/api/eglises.php?user_id=${userId}&parent=filiale&limit=1000`, {
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      if (result.success) setTemples(result.eglises || []);
    } catch (err) { console.error(err); }
  };

  const loadBilanData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) { navigate('/pages/signin2'); return; }
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      let url = `https://josephsbusiness.com/api/bilan_mensuel.php?user_id=${userId}&mois=${selectedMonth}&annee=${selectedYear}`;
      if (selectedTemple !== 'all') url += `&temple=${encodeURIComponent(selectedTemple)}`;

      const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();

      if (result.success) {
        setBilanData(result.data);
        // Réinitialiser les pages lors d'un nouveau chargement
        setCurrentPageEntree(1);
        setCurrentPageDepense(1);
        setSearchEntree('');
        setSearchDepense('');
      } else {
        setError(result.message || 'Erreur de chargement');
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, selectedTemple, navigate]);

  useEffect(() => { fetchTemples(); }, []);
  useEffect(() => { loadBilanData(); }, [loadBilanData]);

  const getTempleName = (matricule) => {
    if (matricule === 'all') return 'Tous les temples';
    const temple = temples.find(t => t.matricule === matricule);
    return temple ? temple.nom : matricule;
  };

  // Filtrer et paginer les entrées
  const filteredEntrees = bilanData.entrees.filter(e => {
    const search = searchEntree.toLowerCase();
    return (
      e.matricule?.toLowerCase().includes(search) ||
      (e.nom_type || e.type)?.toLowerCase().includes(search) ||
      (e.nomeg || e.temple_nom)?.toLowerCase().includes(search)
    );
  });
  const totalEntreesPages = Math.ceil(filteredEntrees.length / itemsPerPage);
  const paginatedEntrees = filteredEntrees.slice(
    (currentPageEntree - 1) * itemsPerPage,
    currentPageEntree * itemsPerPage
  );

  // Filtrer et paginer les dépenses
  const filteredDepenses = bilanData.depenses.filter(d => {
    const search = searchDepense.toLowerCase();
    return (
      d.matricule?.toLowerCase().includes(search) ||
      d.intitule?.toLowerCase().includes(search) ||
      (d.nom_type || d.type)?.toLowerCase().includes(search) ||
      (d.nomeg || d.temple_nom)?.toLowerCase().includes(search)
    );
  });
  const totalDepensesPages = Math.ceil(filteredDepenses.length / itemsPerPage);
  const paginatedDepenses = filteredDepenses.slice(
    (currentPageDepense - 1) * itemsPerPage,
    currentPageDepense * itemsPerPage
  );

  const exportToExcel = () => {
    const templeName = getTempleName(selectedTemple);
    const fileName = `bilan_mensuel_${mois[selectedMonth-1]}_${selectedYear}_${templeName.replace(/\s+/g, '_')}.xlsx`;
    const wb = XLSX.utils.book_new();
    const cn = bilanData.totalEntrees * 0.15;
    const reste = bilanData.totalEntrees * 0.85;

    const summaryData = [
      ['BILAN MENSUEL'],
      [`Période: ${mois[selectedMonth-1]} ${selectedYear}`],
      [`Temple: ${templeName}`],
      [''],
      ['RÉSUMÉ'],
      ['Total Entrées', formatMontant(bilanData.totalEntrees)],
      ['Total Dépenses', formatMontant(bilanData.totalDepenses)],
      ['Solde', formatMontant(bilanData.solde)],
      ['Caisse Nationale (15%)', formatMontant(cn)],
      ['Reste Caisse Locale', formatMontant(reste)],
      [''],
      ['DÉTAIL PAR TYPE']
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, ws1, "Résumé");

    const entreesData = [
      ['ENTRÉES PAR TYPE DE REVENU'],
      ['Type', 'Montant', 'CN (15%)', 'Reste'],
      ...Object.entries(bilanData.parType.entrees).map(([type, montant]) => [type, formatMontant(montant), formatMontant(montant * 0.15), formatMontant(montant * 0.85)])
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(entreesData);
    XLSX.utils.book_append_sheet(wb, ws2, "Entrées par type");

    const depensesData = [
      ['DÉPENSES PAR TYPE'],
      ['Type', 'Montant'],
      ...Object.entries(bilanData.parType.depenses).map(([type, montant]) => [type, formatMontant(montant)])
    ];
    const ws3 = XLSX.utils.aoa_to_sheet(depensesData);
    XLSX.utils.book_append_sheet(wb, ws3, "Dépenses par type");

    XLSX.writeFile(wb, fileName);
  };

  // ===== IMPRESSION PDF MODERNISÉE (iframe) =====
  const printPDF = () => {
    let egliseNom = 'JOSEPHSBUSINESS';
    let egliseLogo = 'logo.jpeg';
    let egliseVille = '';
    let egliseAdresse = '';

    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        egliseNom = user.temple || user.nom_eglise || user.eglise_nom || 'JOSEPHSBUSINESS';
        egliseLogo = user.logo_parent || user.logo || 'logo.jpeg';
        egliseVille = user.ville || '';
        egliseAdresse = user.adresse || '';
      }
    } catch (e) {}

    const templeName = getTempleName(selectedTemple);
    const cn = bilanData.totalEntrees * 0.15;
    const reste = bilanData.totalEntrees * 0.85;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bilan Mensuel - ${egliseNom}</title>
        <meta charset="UTF-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #1e293b; background: white; }
          .header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 3px solid ${COLORS.primary};
            padding-bottom: 15px;
            margin-bottom: 20px;
            flex-wrap: wrap;
          }
          .header-left { display: flex; align-items: center; gap: 15px; }
          .logo { height: 60px; width: auto; border-radius: 50%; border: 2px solid ${COLORS.secondary}; }
          .header-title .company-name { font-size: 20px; font-weight: 700; color: ${COLORS.primary}; }
          .header-title .subtitle { color: #64748b; font-size: 14px; }
          .header-right { text-align: right; font-size: 13px; color: #64748b; }
          .summary {
            background: #f8fafc;
            padding: 12px 20px;
            border-left: 4px solid ${COLORS.primary};
            margin-bottom: 20px;
            display: flex;
            flex-wrap: wrap;
            justify-content: space-around;
            gap: 10px;
          }
          .summary-item { font-size: 14px; }
          .summary-item strong { color: ${COLORS.primary}; }
          .section-title {
            font-size: 16px;
            font-weight: 600;
            margin: 25px 0 12px;
            color: ${COLORS.primary};
            border-left: 3px solid ${COLORS.secondary};
            padding-left: 10px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
            margin-top: 10px;
          }
          th {
            background: ${COLORS.primary};
            color: white;
            padding: 10px 8px;
            text-align: left;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          td {
            padding: 8px;
            border-bottom: 1px solid #e2e8f0;
          }
          tr:nth-child(even) { background: #f8fafc; }
          .montant { text-align: right; font-weight: 600; }
          .text-success { color: ${COLORS.success}; }
          .text-danger { color: ${COLORS.danger}; }
          .text-purple { color: #8b5cf6; }
          .total-row { background: #e2e8f0; font-weight: 700; }
          .total-row td { border-top: 2px solid ${COLORS.primary}; }
          .footer {
            margin-top: 25px;
            text-align: center;
            font-size: 12px;
            color: #94a3b8;
            border-top: 1px solid #e2e8f0;
            padding-top: 15px;
          }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            <img src="https://josephsbusiness.com/assets/images/${egliseLogo}" alt="Logo" class="logo" onerror="this.style.display='none'">
            <div class="header-title">
              <div class="company-name">${egliseNom}</div>
              <div class="subtitle">Bilan Mensuel - ${mois[selectedMonth-1]} ${selectedYear}</div>
            </div>
          </div>
          <div class="header-right">
            <div><strong>Temple</strong> : ${templeName}</div>
            <div style="font-size:12px;">${new Date().toLocaleDateString('fr-FR')}</div>
          </div>
        </div>

        <div class="summary">
          <span class="summary-item"><strong>Total Entrées</strong> : <span class="text-success">${formatMontant(bilanData.totalEntrees)}</span></span>
          <span class="summary-item"><strong>Total Dépenses</strong> : <span class="text-danger">${formatMontant(bilanData.totalDepenses)}</span></span>
          <span class="summary-item"><strong>Solde</strong> : <span class="${bilanData.solde >= 0 ? 'text-success' : 'text-danger'}">${formatMontant(bilanData.solde)}</span></span>
          <span class="summary-item"><strong>CN (15%)</strong> : <span class="text-purple">${formatMontant(cn)}</span></span>
          <span class="summary-item"><strong>Reste Local</strong> : <span class="text-success">${formatMontant(reste)}</span></span>
        </div>

        <div class="section-title">Entrées par type de revenu</div>
        <table>
          <thead><tr><th>Type</th><th>Montant</th><th>CN (15%)</th><th>Reste</th><th>%</th></tr></thead>
          <tbody>
            ${Object.entries(bilanData.parType.entrees).length > 0 ? 
              Object.entries(bilanData.parType.entrees).map(([type, montant]) => {
                const pct = ((montant / bilanData.totalEntrees) * 100).toFixed(1);
                return `<tr>
                  <td>${type}</td>
                  <td class="montant text-success">${formatMontant(montant)}</td>
                  <td class="montant text-purple">${formatMontant(montant * 0.15)}</td>
                  <td class="montant text-success">${formatMontant(montant * 0.85)}</td>
                  <td class="montant">${pct}%</td>
                </tr>`;
              }).join('') : '<tr><td colspan="5" style="text-align:center;">Aucune entrée</td></tr>'
            }
          </tbody>
          <tfoot>
            <tr class="total-row">
              <td>TOTAL ENTRÉES</td>
              <td class="montant text-success">${formatMontant(bilanData.totalEntrees)}</td>
              <td class="montant text-purple">${formatMontant(cn)}</td>
              <td class="montant text-success">${formatMontant(reste)}</td>
              <td class="montant">100%</td>
            </tr>
          </tfoot>
        </table>

        <div class="section-title">Dépenses par type</div>
        <table>
          <thead><tr><th>Type</th><th>Montant</th><th>%</th></tr></thead>
          <tbody>
            ${Object.entries(bilanData.parType.depenses).length > 0 ? 
              Object.entries(bilanData.parType.depenses).map(([type, montant]) => {
                const pct = ((montant / bilanData.totalDepenses) * 100).toFixed(1);
                return `<tr>
                  <td>${type}</td>
                  <td class="montant text-danger">${formatMontant(montant)}</td>
                  <td class="montant">${pct}%</td>
                </tr>`;
              }).join('') : '<tr><td colspan="3" style="text-align:center;">Aucune dépense</td></tr>'
            }
          </tbody>
          <tfoot>
            <tr class="total-row">
              <td>TOTAL DÉPENSES</td>
              <td class="montant text-danger">${formatMontant(bilanData.totalDepenses)}</td>
              <td class="montant">100%</td>
            </tr>
          </tfoot>
        </table>

        <div class="footer">
          Document généré automatiquement • ${new Date().toLocaleDateString('fr-FR')} • ${egliseNom}
        </div>
      </body>
      </html>
    `;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(htmlContent);
    iframeDoc.close();

    iframe.contentWindow.onload = () => {
      iframe.contentWindow.print();
      setTimeout(() => {
        if (document.body.contains(iframe)) document.body.removeChild(iframe);
      }, 1000);
    };
  };

if (loading) {
  return (
    <>
    <style>{styles}</style>
      <Header />
      <Sidebar />
      <div 
        className="main main-app p-3 p-lg-4 dashboard-styled d-flex justify-content-center align-items-center" 
        style={{ minHeight: 'calc(100vh - 140px)' }}
      >
        <div className="text-center container-fluid px-0" style={{ maxWidth: '420px', padding: '2rem',textAlign:'center' }}>
          {/* Icône animée */}
          
          {/* Titre avec points de suspension animés */}
          <h5 className="fw-bold mb-2" style={{ color: COLORS.primaryDark }}>
            Chargement en cours
            <span className="loading-dots">
              <span>.</span><span>.</span><span>.</span>
            </span>
          </h5>
          <p className="text-muted small mb-4">Récupération du bilan annuel, veuillez patienter...</p>

          {/* Barre de progression animée */}
          <div className="loading-progress-wrapper mx-auto" style={{ width: '80%' }}>
            <div
              className="loading-progress-track"
              style={{
                height: '4px',
                borderRadius: '4px',
                background: '#e9edf2',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              <div
                className="loading-progress-bar"
                style={{
                  height: '100%',
                  width: '0%',
                  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                  backgroundSize: '200% 100%',
                  borderRadius: '4px',
                  animation: 'loadingProgress 2s ease-in-out infinite, shimmer 1.5s linear infinite'
                }}
              />
            </div>
          </div>

          {/* Badge de statut */}
          <div className="mt-3">
            <span
              className="badge"
              style={{
                background: `${COLORS.primary}10`,
                color: COLORS.primary,
                borderRadius: '4px',
                padding: '0.3rem 1rem',
                fontSize: '0.7rem',
                fontWeight: '500',
                border: `1px solid ${COLORS.primary}20`
              }}
            >
              <i className="ri-loader-4-line me-1" style={{ animation: 'spin 1s linear infinite' }}></i>
              Préparation...
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes loadingProgress {
          0% { width: 0%; }
          50% { width: 70%; }
          100% { width: 100%; }
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes pulseGlow {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        .loading-logo-pulse {
          animation: pulseGlow 1.8s ease-in-out infinite;
        }
        .loading-logo-circle {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 32px rgba(41, 90, 167, 0.12);
          border: 2px solid ${COLORS.primary}20;
          animation: float 2.5s ease-in-out infinite;
          position: relative;
        }
        .loading-logo-circle::after {
          content: '';
          position: absolute;
          top: -3px;
          left: -3px;
          right: -3px;
          bottom: -3px;
          border-radius: 50%;
          border: 2px solid transparent;
          border-top-color: ${COLORS.primary};
          border-right-color: ${COLORS.secondary};
          animation: spin 1.2s linear infinite;
        }
        .loading-dots span {
          display: inline-block;
          animation: bounce 1.4s ease-in-out infinite;
          font-size: 1.6rem;
          line-height: 1;
          margin-left: 1px;
        }
        .loading-dots span:nth-child(1) { animation-delay: 0s; }
        .loading-dots span:nth-child(2) { animation-delay: 0.2s; }
        .loading-dots span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(1); opacity: 0.3; }
          40% { transform: scale(1.2); opacity: 1; }
        }
        .loading-progress-track {
          box-shadow: inset 0 1px 3px rgba(0,0,0,0.05);
        }
      `}</style>

      <Footer />
    </>
  );
}

  const cn = bilanData.totalEntrees * 0.15;
  const reste = bilanData.totalEntrees * 0.85;

  return (
    <>
      <style>{styles}</style>
      <Header />
      <Sidebar />
      <div className="main main-app p-3 p-lg-4 dashboard-styled">
        <div className="container-fluid px-0">

          {/* ===== EN‑TÊTE MODERNE ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <i className="ri-pie-chart-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Bilan Mensuel</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-calendar-line"></i> {mois[selectedMonth-1]} {selectedYear}
                  </span>
                  <span className="badge-modern">
                    <i className="ri-building-line"></i> {getTempleName(selectedTemple)}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button size="sm" className="btn-modern-success" onClick={exportToExcel}>
                <i className="ri-file-excel-line"></i> Excel
              </Button>
              <Button size="sm" className="btn-modern-outline" onClick={printPDF}>
                <i className="ri-file-pdf-line"></i> PDF
              </Button>
            </div>
          </div>

          {/* ===== FILTRES ===== */}
          <Card className="filter-card mb-4 fade-in">
            <Card.Body className="p-3">
              <Row className="g-2 align-items-end">
                <Col md={3}>
                  <Form.Label className="fw-semibold text-white small" style={{ opacity: 0.8 }}>Mois</Form.Label>
                  <Form.Select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
                    {mois.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label className="fw-semibold text-white small" style={{ opacity: 0.8 }}>Année</Form.Label>
                  <Form.Select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-semibold text-white small" style={{ opacity: 0.8 }}>Temple</Form.Label>
                  <Form.Select value={selectedTemple} onChange={(e) => setSelectedTemple(e.target.value)}>
                    <option value="all">Tous les temples</option>
                    {temples.map(t => <option key={t.matricule} value={t.matricule}>{t.nom}</option>)}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Button size="sm" style={{background:Colors.secondary}} className="w-100" onClick={loadBilanData}>
                    <i className="ri-refresh-line me-1"></i> Actualiser
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {error && (
            <Alert variant="danger" className="border-0 shadow-sm rounded-3 mb-3" style={{ borderRadius: '4px' }}>
              <i className="ri-error-warning-line me-2"></i> {error}
            </Alert>
          )}

          {/* ===== KPI CARDS ===== */}
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-success">
                <div className="kpi-icon"><i className="ri-money-euro-circle-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Total Entrées</span>
                  <h3 className="kpi-value" style={{ color: COLORS.success }}>{formatMontant(bilanData.totalEntrees)}</h3>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-danger">
                <div className="kpi-icon"><i className="ri-money-euro-circle-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Total Dépenses</span>
                  <h3 className="kpi-value" style={{ color: COLORS.danger }}>{formatMontant(bilanData.totalDepenses)}</h3>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-primary">
                <div className="kpi-icon"><i className="ri-funds-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Solde</span>
                  <h3 className="kpi-value" style={{ color: bilanData.solde >= 0 ? COLORS.success : COLORS.danger }}>{formatMontant(bilanData.solde)}</h3>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-purple">
                <div className="kpi-icon"><i className="ri-bank-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Caisse Nationale (15%)</span>
                  <h3 className="kpi-value" style={{ color: '#8b5cf6' }}>{formatMontant(cn)}</h3>
                </div>
              </div>
            </Col>
          </Row>

          {/* ===== GRAPHIQUES ===== */}
          <Row className="g-4 mb-4">
            <Col md={6} className="d-flex">
              <div className="chart-card w-100 d-flex flex-column">
                <div className="chart-card-header d-flex justify-content-between align-items-center">
                  <span>Entrées par type</span>
                  <Badge style={{ background: COLORS.success, borderRadius: '4px' }}>Total: {formatMontant(bilanData.totalEntrees)}</Badge>
                </div>
                <div className="p-3 flex-grow-1">
                  {Object.keys(bilanData.parType.entrees).length > 0 ? (
                    Object.entries(bilanData.parType.entrees).map(([type, montant], idx) => {
                      const pct = ((montant / bilanData.totalEntrees) * 100).toFixed(1);
                      return (
                        <div key={idx} className="mb-2">
                          <div className="d-flex justify-content-between small">
                            <span>{type}</span>
                            <span className="fw-semibold" style={{ color: COLORS.success }}>{pct}%</span>
                          </div>
                          <div className="progress-bar-container">
                            <div className="progress-bar-fill" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${COLORS.success}, ${COLORS.primary})` }} />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-muted small text-center py-3">Aucune entrée</p>
                  )}
                </div>
              </div>
            </Col>
            <Col md={6} className="d-flex">
              <div className="chart-card w-100 d-flex flex-column">
                <div className="chart-card-header d-flex justify-content-between align-items-center">
                  <span>Dépenses par type</span>
                  <Badge style={{ background: COLORS.danger, borderRadius: '4px' }}>Total: {formatMontant(bilanData.totalDepenses)}</Badge>
                </div>
                <div className="p-3 flex-grow-1">
                  {Object.keys(bilanData.parType.depenses).length > 0 ? (
                    Object.entries(bilanData.parType.depenses).map(([type, montant], idx) => {
                      const pct = ((montant / bilanData.totalDepenses) * 100).toFixed(1);
                      return (
                        <div key={idx} className="mb-2">
                          <div className="d-flex justify-content-between small">
                            <span>{type}</span>
                            <span className="fw-semibold" style={{ color: COLORS.danger }}>{pct}%</span>
                          </div>
                          <div className="progress-bar-container">
                            <div className="progress-bar-fill" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${COLORS.danger}, ${COLORS.warning})` }} />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-muted small text-center py-3">Aucune dépense</p>
                  )}
                </div>
              </div>
            </Col>
          </Row>

          {/* ===== DÉTAIL DES ENTRÉES AVEC RECHERCHE ET PAGINATION ===== */}
          <Row className="g-4 mb-4">
            <Col md={12}>
              <Card className="table-card">
                <div className="table-header-secondary d-flex justify-content-between align-items-center px-3 py-2 flex-wrap">
                  <span className="fw-semibold"><i className="ri-money-euro-circle-line me-1" style={{ color: COLORS.primary }}></i> Détail des entrées</span>
                  <div className="d-flex align-items-center gap-2">
                    <InputGroup size="sm" style={{ width: '250px' }}>
                      <InputGroup.Text className="bg-white">
                        <i className="ri-search-line text-muted"></i>
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Rechercher..."
                        value={searchEntree}
                        onChange={(e) => {
                          setSearchEntree(e.target.value);
                          setCurrentPageEntree(1);
                        }}
                      />
                    </InputGroup>
                    <Badge bg="light" text="dark" className="rounded-pill px-3">{filteredEntrees.length} résultats</Badge>
                  </div>
                </div>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle table-modern">
                      <thead>
                        <tr>
                          <th className="px-2">Date</th>
                          <th className="px-2">Matricule</th>
                          <th className="px-2">Type</th>
                          <th className="px-2">Montant</th>
                          <th className="px-2">CN (15%)</th>
                          <th className="px-2">Reste</th>
                          <th className="px-2">Temple</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedEntrees.length > 0 ? (
                          paginatedEntrees.map((e, idx) => (
                            <tr key={idx}>
                              <td className="px-2">{new Date(e.date_at).toLocaleDateString('fr-FR')}</td>
                              <td className="px-2"><span className="reference-badge">{e.matricule}</span></td>
                              <td className="px-2">{e.nom_type || e.type}</td>
                              <td className="px-2 fw-bold" style={{ color: COLORS.success }}>{formatMontant(e.total)}</td>
                              <td className="px-2" style={{ color: '#8b5cf6' }}>{formatMontant(e.total * 0.15)}</td>
                              <td className="px-2" style={{ color: COLORS.success }}>{formatMontant(e.total * 0.85)}</td>
                              <td className="px-2">{e.nomeg || e.temple_nom}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan="7" className="text-center py-4 text-muted">Aucune entrée trouvée</td></tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
                {totalEntreesPages > 1 && (
                  <Card.Footer className="border-0 bg-transparent pt-2 pb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">
                        Page {currentPageEntree} sur {totalEntreesPages}
                      </small>
                      <Pagination className="pagination-modern mb-0" size="sm">
                        <Pagination.First onClick={() => setCurrentPageEntree(1)} disabled={currentPageEntree === 1} />
                        <Pagination.Prev onClick={() => setCurrentPageEntree(p => Math.max(1, p - 1))} disabled={currentPageEntree === 1} />
                        {[...Array(Math.min(5, totalEntreesPages))].map((_, i) => {
                          let page = i + 1;
                          if (totalEntreesPages > 5) {
                            if (currentPageEntree > 3) page = currentPageEntree - 2 + i;
                            if (page > totalEntreesPages) page = totalEntreesPages - 4 + i;
                          }
                          return (
                            <Pagination.Item
                              key={page}
                              active={page === currentPageEntree}
                              onClick={() => setCurrentPageEntree(page)}
                            >
                              {page}
                            </Pagination.Item>
                          );
                        })}
                        <Pagination.Next onClick={() => setCurrentPageEntree(p => Math.min(totalEntreesPages, p + 1))} disabled={currentPageEntree === totalEntreesPages} />
                        <Pagination.Last onClick={() => setCurrentPageEntree(totalEntreesPages)} disabled={currentPageEntree === totalEntreesPages} />
                      </Pagination>
                    </div>
                  </Card.Footer>
                )}
              </Card>
            </Col>
          </Row>

          {/* ===== DÉTAIL DES DÉPENSES AVEC RECHERCHE ET PAGINATION ===== */}
          <Row className="g-4">
            <Col md={12}>
              <Card className="table-card">
                <div className="table-header-secondary d-flex justify-content-between align-items-center px-3 py-2 flex-wrap">
                  <span className="fw-semibold"><i className="ri-money-euro-circle-line me-1" style={{ color: COLORS.danger }}></i> Détail des dépenses</span>
                  <div className="d-flex align-items-center gap-2">
                    <InputGroup size="sm" style={{ width: '250px' }}>
                      <InputGroup.Text className="bg-white">
                        <i className="ri-search-line text-muted"></i>
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Rechercher..."
                        value={searchDepense}
                        onChange={(e) => {
                          setSearchDepense(e.target.value);
                          setCurrentPageDepense(1);
                        }}
                      />
                    </InputGroup>
                    <Badge bg="light" text="dark" className="rounded-pill px-3">{filteredDepenses.length} résultats</Badge>
                  </div>
                </div>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle table-modern">
                      <thead>
                        <tr>
                          <th className="px-2">Date</th>
                          <th className="px-2">Matricule</th>
                          <th className="px-2">Intitulé</th>
                          <th className="px-2">Type</th>
                          <th className="px-2">Montant</th>
                          <th className="px-2">Compte</th>
                          <th className="px-2">Temple</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedDepenses.length > 0 ? (
                          paginatedDepenses.map((d, idx) => (
                            <tr key={idx}>
                              <td className="px-2">{new Date(d.date_at).toLocaleDateString('fr-FR')}</td>
                              <td className="px-2"><span className="reference-badge">{d.matricule}</span></td>
                              <td className="px-2">{d.intitule}</td>
                              <td className="px-2">{d.nom_type || d.type}</td>
                              <td className="px-2 fw-bold" style={{ color: COLORS.danger }}>{formatMontant(d.montant)}</td>
                              <td className="px-2">
                                <span className="badge" style={{ background: d.compte === 'La Banque' ? COLORS.secondary : COLORS.primary, borderRadius: '4px', color: '#fff' }}>
                                  {d.compte || '-'}
                                </span>
                              </td>
                              <td className="px-2">{d.nomeg || d.temple_nom}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan="7" className="text-center py-4 text-muted">Aucune dépense trouvée</td></tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
                {totalDepensesPages > 1 && (
                  <Card.Footer className="border-0 bg-transparent pt-2 pb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">
                        Page {currentPageDepense} sur {totalDepensesPages}
                      </small>
                      <Pagination className="pagination-modern mb-0" size="sm">
                        <Pagination.First onClick={() => setCurrentPageDepense(1)} disabled={currentPageDepense === 1} />
                        <Pagination.Prev onClick={() => setCurrentPageDepense(p => Math.max(1, p - 1))} disabled={currentPageDepense === 1} />
                        {[...Array(Math.min(5, totalDepensesPages))].map((_, i) => {
                          let page = i + 1;
                          if (totalDepensesPages > 5) {
                            if (currentPageDepense > 3) page = currentPageDepense - 2 + i;
                            if (page > totalDepensesPages) page = totalDepensesPages - 4 + i;
                          }
                          return (
                            <Pagination.Item
                              key={page}
                              active={page === currentPageDepense}
                              onClick={() => setCurrentPageDepense(page)}
                            >
                              {page}
                            </Pagination.Item>
                          );
                        })}
                        <Pagination.Next onClick={() => setCurrentPageDepense(p => Math.min(totalDepensesPages, p + 1))} disabled={currentPageDepense === totalDepensesPages} />
                        <Pagination.Last onClick={() => setCurrentPageDepense(totalDepensesPages)} disabled={currentPageDepense === totalDepensesPages} />
                      </Pagination>
                    </div>
                  </Card.Footer>
                )}
              </Card>
            </Col>
          </Row>

          <Footer />
        </div>
      </div>
    </>
  );
}